import React from 'react';
import type { PingSummary } from '../types/diagnostics';

interface Props {
  ping: PingSummary;
}

export const JitterHeatmap: React.FC<Props> = ({ ping }) => {
  const rtts = ping.rtts;
  if (rtts.length < 2) return null;

  // Calculate jitter between consecutive pings
  const jitters: number[] = [];
  for (let i = 1; i < rtts.length; i++) {
    jitters.push(Math.abs(rtts[i] - rtts[i - 1]));
  }

  const maxJitter = Math.max(...jitters, 1);

  // Create grid (6 columns)
  const cols = 6;
  const rows = Math.ceil(jitters.length / cols);

  return (
    <div className="bg-dark-900 rounded-xl p-6 border border-dark-700">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
          Jitter Heatmap
        </h3>
        <div className="text-xs text-slate-500">
          Avg: <span className="text-purple-400">{ping.jitter.toFixed(1)} ms</span>
          {' · '}
          Max: <span className="text-red-400">{maxJitter.toFixed(1)} ms</span>
        </div>
      </div>

      {/* Heatmap grid */}
      <div
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
      >
        {jitters.map((j, i) => {
          const intensity = j / maxJitter;
          const color = getHeatColor(intensity);
          return (
            <div
              key={i}
              className="aspect-square rounded-sm relative group cursor-pointer"
              style={{ backgroundColor: color }}
              title={`Ping ${i + 1}→${i + 2}: ${j.toFixed(1)}ms jitter`}
            >
              {/* Tooltip on hover */}
              <div className="absolute z-10 bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block">
                <div className="bg-dark-800 border border-dark-700 rounded px-2 py-1 text-[10px] text-white whitespace-nowrap shadow-lg">
                  #{i + 1} → #{i + 2}<br />
                  Jitter: {j.toFixed(1)}ms
                </div>
              </div>
            </div>
          );
        })}

        {/* Fill remaining cells */}
        {Array.from({ length: rows * cols - jitters.length }).map((_, i) => (
          <div key={`empty-${i}`} className="aspect-square rounded-sm bg-dark-850" />
        ))}
      </div>

      {/* Legend */}
      <div className="mt-3 flex items-center gap-2 justify-center">
        <span className="text-[10px] text-slate-500">Low</span>
        <div className="flex gap-0.5">
          {[0, 0.2, 0.4, 0.6, 0.8, 1.0].map((v) => (
            <div
              key={v}
              className="w-6 h-3 rounded-sm"
              style={{ backgroundColor: getHeatColor(v) }}
            />
          ))}
        </div>
        <span className="text-[10px] text-slate-500">High</span>
      </div>
    </div>
  );
};

function getHeatColor(intensity: number): string {
  // Blue → Green → Yellow → Red
  if (intensity < 0.25) {
    const t = intensity / 0.25;
    return `rgb(${Math.round(30 + t * 20)}, ${Math.round(80 + t * 120)}, ${Math.round(180 - t * 40)})`;
  }
  if (intensity < 0.5) {
    const t = (intensity - 0.25) / 0.25;
    return `rgb(${Math.round(50 + t * 150)}, ${Math.round(200 - t * 20)}, ${Math.round(140 - t * 100)})`;
  }
  if (intensity < 0.75) {
    const t = (intensity - 0.5) / 0.25;
    return `rgb(${Math.round(200 + t * 45)}, ${Math.round(180 - t * 80)}, ${Math.round(40 - t * 20)})`;
  }
  const t = (intensity - 0.75) / 0.25;
  return `rgb(${Math.round(245 - t * 15)}, ${Math.round(100 - t * 60)}, ${Math.round(20 + t * 10)})`;
}
